import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useAnchoringDocuments,
  type UnanchoredDocumentRow,
} from "~/api/v2/useAnchoringDocuments";
import { useBulkSelection } from "~/hooks/useBulkSelection";
import { Button } from "~/atoms/button";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import { AnchoringTable, type AnchoringColumn } from "./AnchoringTable";
import CreateQuizzesDialog from "./CreateQuizzesDialog";
import { releaseColumns, titleColumn } from "./cells";

export const UnanchoredDocumentsTab = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { unanchored, isLoading, error } = useAnchoringDocuments();
  const selection = useBulkSelection(
    unanchored.map((row) => row.documentReleaseId),
  );
  const [isCreateOpen, setCreateOpen] = useState(false);

  if (isLoading) return <CenterSpinner />;
  if (error) return <ErrorState />;

  const selectedDocuments = unanchored
    .filter((row) => selection.selectedIds.has(row.documentReleaseId))
    .map((row) => ({
      documentId: row.documentId,
      documentReleaseId: row.documentReleaseId,
      title: row.title,
    }));

  const columns: AnchoringColumn<UnanchoredDocumentRow>[] = [
    titleColumn(t),
    ...releaseColumns(t),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          disabled={selection.selectedIds.size === 0}
          onClick={() => setCreateOpen(true)}
        >
          {t(($) => $.anchoring.actions.createQuizzes)}
        </Button>
      </div>
      <AnchoringTable
        columns={columns}
        rows={unanchored}
        getRowId={(row) => row.documentReleaseId}
        emptyMessage={t(($) => $.anchoring.empty.unanchored)}
        selection={selection}
      />
      {isCreateOpen && (
        <CreateQuizzesDialog
          documents={selectedDocuments}
          open={isCreateOpen}
          onOpenChange={setCreateOpen}
          onCreated={() => {
            selection.clearSelection();
            void navigate("/anchoring/quizzes");
          }}
        />
      )}
    </div>
  );
};
